import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Autocomplete,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
} from '@mui/icons-material';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { employeeApi, Employee } from '../services/employeeService.ts';
import { sickFormApi, SickEntry } from '../services/sickFormService.ts';
import { tipoutApi } from '../services/tipoutService.ts';

const emptyForm = {
  employeeId: '',
  employeeName: '',
  designation: '',
  fromDate: '',
  toDate: '',
  reason: '',
};

const headerCellSx = {
  color: '#fff',
  fontWeight: 700,
  fontSize: '0.8rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  border: '2px solid #7f0000',
  py: 1.5,
  px: 1.5,
  whiteSpace: 'nowrap' as const,
};

const calcDays = (from: string, to: string): number => {
  if (!from || !to) return 0;
  const diff = new Date(to).getTime() - new Date(from).getTime();
  return diff < 0 ? 0 : Math.round(diff / (1000 * 60 * 60 * 24)) + 1;
};

const formatDate = (d: string) =>
  d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const formatCreatedDate = (createdDate: number) =>
  new Date(createdDate * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

// Formats a Date as YYYY-MM-DD in the business's Massachusetts timezone (matches the backend's "current date")
const formatMassachusettsDate = (date: Date): string => {
  const massTime = new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const year = massTime.getFullYear();
  const month = String(massTime.getMonth() + 1).padStart(2, '0');
  const day = String(massTime.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const SickFormPage: React.FC = () => {
  const { showSuccess, showError } = useSnackbar();
  const { sessionToken, user } = useAuth();
  const isAdmin = user?.type === 'admin';
  const [entries, setEntries] = useState<SickEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<SickEntry | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveError, setAutoSaveError] = useState('');
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, []);

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  // Current business date, used to restrict edits to the day an entry was created
  const [currentDate, setCurrentDate] = useState<string>('');

  const isEditableToday = (entry: SickEntry): boolean => {
    if (!currentDate) return false;
    const entryDateStr = formatMassachusettsDate(new Date(entry.createdDate * 1000));
    return entryDateStr === currentDate;
  };

  const fetchEntries = async () => {
    if (!sessionToken) return;
    setLoading(true);
    try {
      const res = await sickFormApi.getAll({ page: 1, limit: 500 }, sessionToken);
      setEntries(res.data);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to load entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!sessionToken) return;
    fetchEntries();
    setEmpLoading(true);
    employeeApi.getEmployees({ page: 1, limit: 1000 }, sessionToken)
      .then(res => setEmployees(res.data))
      .catch(() => {})
      .finally(() => setEmpLoading(false));
    tipoutApi.getCurrentDate(sessionToken)
      .then(res => setCurrentDate(res.date))
      .catch(() => {});
  }, [sessionToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const openAdd = () => {
    setEditingEntry(null);
    setSelectedEmployee(null);
    setForm({ ...emptyForm, fromDate: new Date().toISOString().slice(0, 10) });
    setDialogOpen(true);
  };

  const openEdit = (entry: SickEntry) => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
    setAutoSaveStatus('idle');
    setAutoSaveError('');
    setEditingEntry(entry);
    const emp = employees.find(e => e.employeeId === entry.employeeId && e.name === entry.employeeName) ?? null;
    setSelectedEmployee(emp);
    setForm({
      employeeId: entry.employeeId,
      employeeName: entry.employeeName,
      designation: entry.designation,
      fromDate: entry.fromDate,
      toDate: entry.toDate,
      reason: entry.reason,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!sessionToken || !window.confirm('Delete this sick day entry?')) return;
    try {
      await sickFormApi.remove(id, sessionToken);
      showSuccess('Entry deleted.');
      fetchEntries();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete entry');
    }
  };

  const handleStatusChange = async (id: number, status: 'approved' | 'rejected') => {
    if (!sessionToken) return;
    try {
      await sickFormApi.updateStatus(id, status, sessionToken);
      showSuccess(`Entry ${status}.`);
      fetchEntries();
    } catch (err) {
      showError(err instanceof Error ? err.message : `Failed to ${status === 'approved' ? 'approve' : 'reject'} entry`);
    }
  };

  const validateForm = (f: typeof emptyForm, employeeSelected: boolean): string | null => {
    if (!employeeSelected || !f.fromDate || !f.toDate || !f.reason.trim()) {
      return 'Please select an employee and fill in all fields.';
    }
    if (new Date(f.toDate) < new Date(f.fromDate)) {
      return 'To Date must be on or after From Date.';
    }
    return null;
  };

  const buildPayload = (f: typeof emptyForm) => ({
    employeeId: f.employeeId.trim(),
    employeeName: f.employeeName.trim(),
    designation: f.designation.trim(),
    fromDate: f.fromDate,
    toDate: f.toDate,
    numOfDays: calcDays(f.fromDate, f.toDate),
    reason: f.reason.trim(),
  });

  // Auto-saves ~900ms after the last change to any field, while editing an existing
  // entry. Adding a new entry still requires the explicit Add button.
  const performAutoSave = useCallback(
    async (id: number, snapshot: typeof emptyForm) => {
      if (!sessionToken) return;
      const validationError = validateForm(snapshot, Boolean(snapshot.employeeId));
      if (validationError) {
        setAutoSaveStatus('error');
        setAutoSaveError(validationError);
        return;
      }
      setAutoSaveStatus('saving');
      try {
        await sickFormApi.update(id, buildPayload(snapshot), sessionToken);
        setAutoSaveStatus('saved');
        fetchEntries();
      } catch (err) {
        setAutoSaveStatus('error');
        setAutoSaveError(err instanceof Error ? err.message : 'Failed to save changes');
      }
    },
    [sessionToken]
  );

  const scheduleAutoSave = (next: typeof emptyForm) => {
    if (!editingEntry) return;
    const entryId = editingEntry.id;
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    setAutoSaveStatus('pending');
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveTimeoutRef.current = null;
      performAutoSave(entryId, next);
    }, 900);
  };

  const handleFieldChange = (field: keyof typeof emptyForm, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      scheduleAutoSave(next);
      return next;
    });
  };

  const handleCloseDialog = async () => {
    if (autoSaveTimeoutRef.current && editingEntry) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
      await performAutoSave(editingEntry.id, form);
    }
    setDialogOpen(false);
  };

  const handleSave = async () => {
    if (!sessionToken) return;
    const validationError = validateForm(form, Boolean(selectedEmployee));
    if (validationError) {
      showError(validationError);
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload(form);
      if (editingEntry) {
        await sickFormApi.update(editingEntry.id, payload, sessionToken);
        showSuccess('Entry updated.');
      } else {
        await sickFormApi.create(payload, sessionToken);
        showSuccess('Entry added.');
      }
      setDialogOpen(false);
      fetchEntries();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
        <Typography variant="h6" sx={{ color: '#7f0000', fontWeight: 700, flex: '0 0 auto', whiteSpace: 'nowrap' }}>
          Date: {currentDate ? formatDate(currentDate) : ''}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          Sick Leave Log
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAdd}
          sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}
        >
          Add Entry
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {entries.length} entr{entries.length !== 1 ? 'ies' : 'y'}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden', border: '2px solid #9e9e9e' }}>
          <TableContainer>
            <Table sx={{ borderCollapse: 'collapse', '& td': { border: '2px solid #9e9e9e', py: 1.25, px: 1.5 } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                  <TableCell sx={headerCellSx}>ID</TableCell>
                  <TableCell sx={headerCellSx}>Date Entered</TableCell>
                  <TableCell sx={headerCellSx}>Employee ID</TableCell>
                  <TableCell sx={headerCellSx}>Employee Name</TableCell>
                  <TableCell sx={headerCellSx}>Designation</TableCell>
                  <TableCell sx={headerCellSx}>From Date</TableCell>
                  <TableCell sx={headerCellSx}>To Date</TableCell>
                  <TableCell align="center" sx={headerCellSx}>No. of Days</TableCell>
                  <TableCell sx={headerCellSx}>Reason</TableCell>
                  <TableCell align="center" sx={headerCellSx}>Status</TableCell>
                  <TableCell align="center" sx={headerCellSx}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 5, border: '2px solid #9e9e9e', color: 'text.secondary' }}>
                      No sick day entries yet. Click "Add Entry" to get started.
                    </TableCell>
                  </TableRow>
                )}
                {entries.map((entry, idx) => (
                  <TableRow
                    key={entry.id}
                    sx={{
                      backgroundColor: idx % 2 === 0 ? '#fff' : '#fdf5f5',
                      '&:hover': { backgroundColor: '#fce4e4' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{entry.id}</Typography>
                    </TableCell>
                    <TableCell>{formatCreatedDate(entry.createdDate)}</TableCell>
                    <TableCell>{entry.employeeId}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{entry.employeeName}</TableCell>
                    <TableCell>{entry.designation}</TableCell>
                    <TableCell>{formatDate(entry.fromDate)}</TableCell>
                    <TableCell>{formatDate(entry.toDate)}</TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{
                          display: 'inline-block',
                          backgroundColor: '#b71c1c',
                          color: '#fff',
                          borderRadius: 1,
                          px: 1,
                          py: 0.25,
                          fontSize: '0.78rem',
                        }}
                      >
                        {entry.numOfDays}
                      </Typography>
                    </TableCell>
                    <TableCell>{entry.reason}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                        size="small"
                        color={entry.status === 'approved' ? 'success' : entry.status === 'rejected' ? 'error' : 'default'}
                        variant={entry.status === 'pending' ? 'outlined' : 'filled'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {isAdmin && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton size="small" color="success" onClick={() => handleStatusChange(entry.id, 'approved')}>
                              <ApproveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton size="small" color="error" onClick={() => handleStatusChange(entry.id, 'rejected')}>
                              <RejectIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {isAdmin && isEditableToday(entry) ? (
                        <Tooltip title="Edit">
                          <IconButton size="small" color="primary" onClick={() => openEdit(entry)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title={!isAdmin ? 'Only admins can update entries' : 'Entries can only be updated on the day they were entered'}>
                          <span>
                            <IconButton size="small" disabled>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {isAdmin ? (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(entry.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Only admins can delete entries">
                          <span>
                            <IconButton size="small" disabled>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingEntry ? 'Edit Sick Day Entry' : 'Add Sick Day Entry'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Autocomplete
            options={employees}
            loading={empLoading}
            value={selectedEmployee}
            getOptionLabel={emp => `${emp.name}${emp.employeeId && emp.employeeId !== '0' ? ` (${emp.employeeId})` : ''}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, emp) => {
              setSelectedEmployee(emp);
              const next = {
                ...form,
                employeeId: emp?.employeeId ?? '',
                employeeName: emp?.name ?? '',
                designation: emp?.designation ?? '',
              };
              setForm(next);
              scheduleAutoSave(next);
            }}
            renderInput={params => (
              <TextField
                {...params}
                label="Select Employee"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {empLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Employee ID"
              fullWidth
              value={form.employeeId}
              InputProps={{ readOnly: true }}
              sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
            />
            <TextField
              label="Employee Name"
              fullWidth
              value={form.employeeName}
              InputProps={{ readOnly: true }}
              sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
            />
          </Box>
          <TextField
            label="Designation"
            fullWidth
            value={form.designation}
            onChange={e => handleFieldChange('designation', e.target.value)}
          />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="From Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.fromDate}
              onChange={e => handleFieldChange('fromDate', e.target.value)}
            />
            <TextField
              label="To Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.toDate}
              onChange={e => handleFieldChange('toDate', e.target.value)}
            />
            <Box
              sx={{
                minWidth: 72,
                textAlign: 'center',
                backgroundColor: form.fromDate && form.toDate ? '#b71c1c' : '#f5f5f5',
                color: form.fromDate && form.toDate ? '#fff' : 'text.disabled',
                borderRadius: 2,
                py: 1.5,
                px: 1,
                flexShrink: 0,
              }}
            >
              <Typography variant="h6" fontWeight={700} lineHeight={1}>
                {form.fromDate && form.toDate ? calcDays(form.fromDate, form.toDate) : '—'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>days</Typography>
            </Box>
          </Box>
          <TextField
            label="Reason"
            fullWidth
            multiline
            minRows={2}
            value={form.reason}
            onChange={e => handleFieldChange('reason', e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={editingEntry ? { justifyContent: 'space-between' } : undefined}>
          {editingEntry ? (
            <>
              <Typography
                variant="body2"
                color={autoSaveStatus === 'error' ? 'error' : 'text.secondary'}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}
              >
                {autoSaveStatus === 'saving' && (<><CircularProgress size={14} />Saving…</>)}
                {autoSaveStatus === 'pending' && 'Unsaved changes…'}
                {autoSaveStatus === 'saved' && 'All changes saved'}
                {autoSaveStatus === 'error' && (autoSaveError || 'Failed to save changes')}
              </Typography>
              <Button onClick={handleCloseDialog} variant="contained" disabled={autoSaveStatus === 'saving'}
                sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}>
                Close
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : 'Add'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SickFormPage;
